import api, { USE_MOCK, qs } from './apiClient';
import mockApi from '../mock/backend';

export const documentService = {
  listItems(params) {
    if (USE_MOCK) return mockApi.listItems(params);
    return api.get(`/documents${qs(params)}`);
  },

  get(id) {
    if (USE_MOCK) return mockApi.getDocument(id);
    return api.get(`/documents/${id}`);
  },

  update(id, patch) {
    if (USE_MOCK) return mockApi.updateDocument(id, patch);
    return api.patch(`/documents/${id}`, patch);
  },

  move(id, targetFolderId) {
    if (USE_MOCK) return mockApi.moveDocument(id, targetFolderId);
    return api.post(`/documents/${id}/move`, { targetFolderId });
  },

  copy(id, targetFolderId) {
    if (USE_MOCK) return mockApi.copyDocument(id, targetFolderId);
    return api.post(`/documents/${id}/copy`, { targetFolderId });
  },

  remove(id) {
    if (USE_MOCK) return mockApi.deleteDocument(id);
    return api.delete(`/documents/${id}`);
  },

  restore(id) {
    if (USE_MOCK) return mockApi.restoreDocument(id);
    return api.post(`/documents/${id}/restore`);
  },

  purge(id) {
    if (USE_MOCK) return mockApi.purgeDocument(id);
    return api.delete(`/documents/${id}/permanent`);
  },

  emptyTrash() {
    if (USE_MOCK) return mockApi.emptyTrash();
    return api.post('/documents/trash/empty');
  },

  /**
   * Production flow: request a presigned S3 PUT, upload from the browser, then commit
   * metadata. In mock mode the upload is simulated and only the commit runs.
   */
  async createUploadUrl({ fileName, mimeType, sizeBytes, folderId, documentId }) {
    if (USE_MOCK) return { uploadUrl: null, uploadId: `up-${Date.now()}` };
    // `documentId` turns the presign into a new-version upload for that document.
    return api.post('/documents/upload-url', { fileName, mimeType, sizeBytes, folderId, ...(documentId ? { documentId } : {}) });
  },

  commitUpload(payload) {
    if (USE_MOCK) return mockApi.commitUpload(payload);
    return api.post('/documents', payload);
  },

  async downloadUrl(id) {
    if (USE_MOCK) return { url: null, expiresAt: new Date(Date.now() + 120000).toISOString() };
    return api.get(`/documents/${id}/download-url`);
  },

  /**
   * A signed, short-lived URL for a rendered PDF page, a photo, or a grid thumbnail.
   * `kind: 'none'` means the file type has no rendered preview (Office files show text).
   */
  preview(id, { page = 1, size = 'page' } = {}) {
    if (USE_MOCK) return Promise.resolve({ kind: 'none', page: 1, pageCount: null, url: null, expiresAt: null });
    return api.get(`/documents/${id}/preview${qs({ page, size })}`);
  },

  listVersions(id) {
    if (USE_MOCK) return mockApi.listVersions(id);
    return api.get(`/documents/${id}/versions`);
  },

  restoreVersion(id, versionId) {
    if (USE_MOCK) return mockApi.restoreVersion(id, versionId);
    return api.post(`/documents/${id}/versions/${versionId}/restore`);
  },

  async versionDownloadUrl(id, versionId) {
    if (USE_MOCK) return { url: null, expiresAt: new Date(Date.now() + 120000).toISOString() };
    return api.get(`/documents/${id}/versions/${versionId}/download-url`);
  },

  activity(id) {
    if (USE_MOCK) return mockApi.documentActivity(id);
    return api.get(`/documents/${id}/activity`);
  },

  /**
   * What the server is still doing to a freshly uploaded file.
   *
   * Every stage comes from a status column the pipeline writes, so the upload tray reports
   * the work rather than a timer. Mock mode returns a settled pipeline: pretending to
   * extract text that is never extracted is the exact fiction this replaced.
   */
  processing(id) {
    if (USE_MOCK) return Promise.resolve({ stages: [], settled: true });
    return api.get(`/documents/${id}/processing`);
  },

  /** Download and view counts, derived from the audit trail rather than a counter column. */
  stats(id) {
    if (USE_MOCK) {
      return Promise.resolve({
        downloads: { total: 0, internal: 0, external: 0, people: 0, lastAt: null, lastBy: null },
        views: { total: 0, people: 0 },
      });
    }
    return api.get(`/documents/${id}/stats`);
  },

  toggleFavorite(resourceType, resourceId) {
    if (USE_MOCK) return mockApi.toggleFavorite(resourceType, resourceId);
    // Under /documents, because that is where the route is declared and the router is
    // mounted at that prefix. Folders go through it too, which reads oddly, but moving the
    // endpoint would break any client already calling the real path.
    return api.post('/documents/favorites/toggle', { resourceType, resourceId });
  },

  dashboard() {
    if (USE_MOCK) return mockApi.dashboard();
    return api.get('/dashboard');
  },
};

export default documentService;
